/**
 * ConvertToLeadDialog — Prospect-to-Lead Conversion Confirmation
 * Staff-confirmed workflow: Lead Creation → Portal Invitation → Welcome Email
 * Advisory only: all actions are staff-initiated and staff-confirmed.
 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { X, UserPlus, Mail, Shield, CheckCircle, AlertCircle, Loader2, RefreshCw, ExternalLink, ChevronDown, ChevronUp } from "lucide-react";

import { getToken } from "../../lib/api";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

const WELCOME_PREVIEW = (name, portalUrl) => `Dear ${name || "[First Name]"},

Thank you for contacting Expat Tax Firm and considering our team to assist you with your tax matters. We appreciate the opportunity to learn more about your circumstances and how we may be able to help.

To make the process convenient and secure, we would like to welcome you to our Secure Client Portal. Through the portal, you can communicate with our team, provide requested information, and securely upload documents as we work together to determine the appropriate next steps.

ACCESS YOUR SECURE PORTAL: ${portalUrl || "[Secure Personalized Invitation Link]"}

Once you have activated your access, our team will be able to guide you through any preliminary information needed to evaluate your inquiry.

Thank you again for the trust you have placed in our firm. We look forward to getting to know you and assisting you.

Warm regards,
Client Services Team
Expat Tax Firm · Tax Accounting Network LLC`;

const StatusBadge = ({ status, label }) => {
  const cfg = {
    sent: "bg-emerald-100 text-emerald-700",
    previously_sent: "bg-emerald-100 text-emerald-700",
    active: "bg-emerald-100 text-emerald-700",
    pending: "bg-amber-100 text-amber-700",
    failed: "bg-rose-100 text-rose-700",
    skipped: "bg-slate-100 text-slate-500",
  };
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${cfg[status] || "bg-slate-100 text-slate-500"}`}>
      {status === "sent" || status === "previously_sent" || status === "active" ? <CheckCircle size={11} /> : status === "failed" ? <AlertCircle size={11} /> : null}
      {label || status}
    </span>
  );
};

export default function ConvertToLeadDialog({ conversationId, onClose, onConverted, initialQuickReview }) {
  const [name, setName] = useState(initialQuickReview?.contact_name !== "Unknown" ? initialQuickReview?.contact_name || "" : "");
  const [email, setEmail] = useState(initialQuickReview?.contact_email || "");
  const [teamId, setTeamId] = useState("");
  const [teams, setTeams] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [step, setStep] = useState("confirm"); // confirm | converting | done | error
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/teams`, auth()).then(r => setTeams(Array.isArray(r.data) ? r.data : (r.data?.items || r.data?.teams || []))).catch(() => {});
  }, []);

  const handleConvert = async (isRetry = false) => {
    if (!name.trim() || !email.trim()) {
      setError("Name and email are required.");
      return;
    }
    setStep("converting");
    setError(null);
    if (isRetry) setRetrying(true);
    try {
      const { data } = await axios.post(
        `${API}/api/communications/conversations/${conversationId}/convert-to-lead-invite`,
        { name: name.trim(), email: email.trim(), team_id: teamId || undefined, confirmed: true },
        auth()
      );
      setResult(data);
      setStep("done");
      if (isRetry) setRetrying(false);
      onConverted?.(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message || "Conversion failed. Please try again.";
      setError(msg);
      setStep("confirm");
      if (isRetry) setRetrying(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="convert-lead-dialog">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg"><UserPlus size={18} className="text-blue-600" /></div>
            <div>
              <h2 className="font-heading font-semibold text-slate-900 text-sm">Convert to Lead & Invite</h2>
              <p className="text-xs text-slate-500">Create lead · Send secure portal invitation · Send welcome email</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-700 transition-colors" data-testid="convert-dialog-close">
            <X size={16} />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-5 space-y-4">

          {step === "done" && result ? (
            /* Post-conversion status */
            <div className="space-y-4" data-testid="conversion-status-panel">
              <div className="flex items-center gap-2.5 p-3 bg-emerald-50 rounded-xl border border-emerald-200">
                <CheckCircle size={18} className="text-emerald-600 shrink-0" />
                <span className="text-sm font-semibold text-emerald-800">
                  {result.is_idempotent ? "Already converted — existing record returned" : "Conversion successful"}
                </span>
              </div>

              <div className="divide-y divide-slate-100 rounded-xl border border-slate-200 overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3">
                  <span className="text-sm font-medium text-slate-700">Lead Created</span>
                  <div className="flex items-center gap-2">
                    <StatusBadge status="sent" label="Created" />
                    <span className="text-xs text-slate-500 font-mono">{result.client_number}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between px-4 py-3">
                  <span className="text-sm font-medium text-slate-700">Portal Invitation</span>
                  <StatusBadge status={result.portal_invite_status} label={
                    result.portal_invite_status === "invitation_sent" ? "Sent" :
                    result.portal_invite_status === "failed" ? "Failed" :
                    result.portal_invite_status || "Pending"
                  } />
                </div>
                <div className="flex items-center justify-between px-4 py-3">
                  <span className="text-sm font-medium text-slate-700">Welcome Email</span>
                  <StatusBadge status={result.welcome_email_status} label={
                    result.welcome_email_status === "sent" ? "Sent" :
                    result.welcome_email_status === "failed" ? "Failed" :
                    result.welcome_email_status === "previously_sent" ? "Previously Sent" :
                    result.welcome_email_status || "Pending"
                  } />
                </div>
              </div>

              <p className="text-xs text-slate-500">Next step: Awaiting portal activation by the prospect. Check the CRM lead record for status updates.</p>

              <div className="flex gap-2 pt-1">
                {result.lead_id && (
                  <a href={`/clients/${result.lead_id}`} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:underline" data-testid="open-lead-workspace-link">
                    <ExternalLink size={12} /> Open Lead Workspace
                  </a>
                )}
                <button onClick={onClose} className="ml-auto text-sm px-4 py-1.5 bg-slate-900 text-white rounded-lg hover:bg-slate-700 transition-colors" data-testid="convert-done-close-btn">
                  Close
                </button>
              </div>
            </div>
          ) : (
            /* Confirmation form */
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="text-xs font-semibold text-slate-600 mb-1 block">Prospect Name</label>
                  <input data-testid="convert-name-input" type="text" value={name} onChange={e => setName(e.target.value)}
                    placeholder="Full name" disabled={step === "converting"}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-60" />
                </div>
                <div className="col-span-2">
                  <label className="text-xs font-semibold text-slate-600 mb-1 block">Email Address</label>
                  <input data-testid="convert-email-input" type="email" value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="prospect@example.com" disabled={step === "converting"}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-60" />
                </div>
                <div className="col-span-2">
                  <label className="text-xs font-semibold text-slate-600 mb-1 block">Assign Team <span className="font-normal text-slate-400">(optional — auto-assigned if blank)</span></label>
                  <select data-testid="convert-team-select" value={teamId} onChange={e => setTeamId(e.target.value)}
                    disabled={step === "converting"}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white disabled:opacity-60">
                    <option value="">Auto-assign</option>
                    {teams.map(t => <option key={t.key} value={t.key}>{t.name || t.display_name}</option>)}
                  </select>
                </div>
              </div>

              {/* Workflow summary */}
              <div className="space-y-1.5 p-3 bg-slate-50 rounded-xl border border-slate-200">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">What happens next</p>
                {[
                  { icon: <UserPlus size={12} />, text: "Lead created in CRM with conversation history preserved", color: "text-blue-600" },
                  { icon: <Shield size={12} />, text: "Secure portal invitation token generated (72h expiry)", color: "text-indigo-600" },
                  { icon: <Mail size={12} />, text: "One welcome email sent with secure portal activation link", color: "text-emerald-600" },
                ].map((item, i) => (
                  <div key={i} className={`flex items-start gap-2 text-xs ${item.color}`}>
                    <span className="shrink-0 mt-0.5">{item.icon}</span>
                    <span className="text-slate-600">{item.text}</span>
                  </div>
                ))}
              </div>

              {/* Welcome email preview toggle */}
              <div>
                <button onClick={() => setShowPreview(v => !v)} data-testid="toggle-welcome-preview"
                  className="flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-700 transition-colors">
                  {showPreview ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                  {showPreview ? "Hide" : "Preview"} welcome email
                </button>
                {showPreview && (
                  <pre className="mt-2 p-3 bg-slate-50 rounded-lg text-[10px] text-slate-600 whitespace-pre-wrap leading-relaxed border border-slate-200 max-h-44 overflow-y-auto" data-testid="welcome-email-preview">
                    {WELCOME_PREVIEW(name || "[Name]", "[Secure Portal Link]")}
                  </pre>
                )}
              </div>

              {error && (
                <div className="flex items-start gap-2 p-3 bg-rose-50 rounded-lg border border-rose-200" data-testid="convert-error-banner">
                  <AlertCircle size={14} className="text-rose-500 shrink-0 mt-0.5" />
                  <p className="text-xs text-rose-700">{error}</p>
                </div>
              )}

              <div className="flex gap-2 pt-1">
                <button onClick={onClose} className="flex-1 text-sm px-4 py-2 border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50 transition-colors" disabled={step === "converting"} data-testid="convert-cancel-btn">
                  Cancel
                </button>
                <button onClick={() => handleConvert(false)} disabled={step === "converting" || !name.trim() || !email.trim()}
                  data-testid="convert-confirm-btn"
                  className="flex-1 flex items-center justify-center gap-2 text-sm px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-60">
                  {step === "converting" ? <><Loader2 size={14} className="animate-spin" />Converting…</> : <><UserPlus size={14} />Confirm & Invite</>}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
